import type {
  Appointment,
  Encounter,
  EpisodeOfCare,
  Patient,
  RelatedPerson,
} from '@medplum/fhirtypes';
import { getServiceTypeCode } from '@unify/fhir-model/resources/episode-of-care';
import { isCohortGroupAppointment } from '@/lib/groupTherapy';
import {
  formatFhirPatientDisplayName,
  getAllClientParticipantsFromAppointment,
  getAllPatientParticipantsFromAppointment,
  patientDisplayLabelFromReference,
} from './fhirHelpers';
import type { AttendanceRowMeta } from './participantAttendance';

export function isSharedEpisodeServiceType(serviceTypeCode: string | undefined): boolean {
  return serviceTypeCode === 'couples' || serviceTypeCode === 'family';
}

/** Group / IOP notes are per-client even when the appointment lists many participants. */
export function isPerClientNoteServiceType(serviceTypeCode: string | undefined): boolean {
  return serviceTypeCode === 'group' || serviceTypeCode === 'iop';
}

export function resolveNotePatientId(input: {
  notePatientRef?: string;
  encounter?: Encounter | null;
  episodeOfCare?: EpisodeOfCare | null;
  appointment: Appointment;
}): string | undefined {
  const fromEncounter = input.encounter?.subject?.reference?.replace(/^Patient\//, '').trim();
  if (fromEncounter) return fromEncounter;
  const fromRef = input.notePatientRef?.replace(/^Patient\//, '').trim();
  if (fromRef) return fromRef;
  const fromEoc = input.episodeOfCare?.patient?.reference?.replace(/^Patient\//, '').trim();
  if (fromEoc) return fromEoc;
  const firstAppt = getAllPatientParticipantsFromAppointment(input.appointment)[0];
  return firstAppt?.actorReference?.replace(/^Patient\//, '').trim();
}

function participantIdFromActorReference(ref: string | undefined): string | undefined {
  const trimmed = ref?.trim();
  if (!trimmed) return undefined;
  if (trimmed.startsWith('Patient/')) return trimmed.slice('Patient/'.length).trim() || undefined;
  if (trimmed.startsWith('RelatedPerson/')) {
    return trimmed.slice('RelatedPerson/'.length).trim() || undefined;
  }
  return undefined;
}

function displayNameForPatientId(
  appointment: Appointment,
  patientId: string,
  fallback?: string,
): string {
  const match = getAllClientParticipantsFromAppointment(appointment).find(
    (p) => participantIdFromActorReference(p.actorReference) === patientId,
  );
  if (match?.displayName?.trim()) return match.displayName.trim();
  return patientDisplayLabelFromReference(`Patient/${patientId}`, fallback);
}

/**
 * Prefer loaded Patient / RelatedPerson.name over Appointment.participant.display
 * (often missing on intake holds and ad-hoc RelatedPerson guests).
 */
export function enrichAttendanceRowDisplayNames(
  rows: AttendanceRowMeta[],
  patientsById: Record<string, Patient | undefined>,
  relatedPersonsById: Record<string, RelatedPerson | undefined> = {},
): AttendanceRowMeta[] {
  return rows.map((row) => {
    const fromPatient = formatFhirPatientDisplayName(patientsById[row.patientId]);
    if (fromPatient) return { ...row, displayName: fromPatient };
    const fromRelatedPerson = formatFhirPatientDisplayName(relatedPersonsById[row.patientId]);
    return {
      ...row,
      displayName: fromRelatedPerson ?? row.displayName,
    };
  });
}

function rowForPatientId(
  appointment: Appointment,
  patientId: string,
  fallbackDisplay?: string,
): AttendanceRowMeta {
  return {
    patientId,
    displayName: displayNameForPatientId(appointment, patientId, fallbackDisplay),
  };
}

function rowsFromParticipants(
  appointment: Appointment,
  episodeOfCare: EpisodeOfCare | null | undefined,
  includeRelatedPersons: boolean,
): AttendanceRowMeta[] {
  const byId = new Map<string, AttendanceRowMeta>();
  const participants = includeRelatedPersons
    ? getAllClientParticipantsFromAppointment(appointment)
    : getAllPatientParticipantsFromAppointment(appointment);
  for (const p of participants) {
    const participantId = participantIdFromActorReference(p.actorReference);
    if (!participantId) continue;
    byId.set(participantId, { patientId: participantId, displayName: p.displayName });
  }
  const primaryId = episodeOfCare?.patient?.reference?.replace(/^Patient\//, '').trim();
  if (primaryId && !byId.has(primaryId)) {
    byId.set(
      primaryId,
      rowForPatientId(appointment, primaryId, episodeOfCare?.patient?.display),
    );
  }
  return [...byId.values()];
}

/**
 * Who appears in the note attendance section:
 * - Shared Group Therapy cohort note: every Patient on the Appointment.
 * - Couples / family: Patients + RelatedPerson partners on the appointment (+ EoC primary).
 * - Individual (and other non-cohort) notes: the note's client plus any RelatedPerson
 *   participants on the appointment (ad-hoc guests, family supports).
 * - Patient-subject Group / IOP copies: the note's client only.
 *
 * `isSharedCohortNote` must be set by the note UI — chart `patientRef` is often still
 * `Patient/…` for shared notes (page context), which must not force the per-client path.
 */
export function resolveParticipantAttendanceRows(input: {
  appointment: Appointment;
  encounter?: Encounter | null;
  episodeOfCare?: EpisodeOfCare | null;
  notePatientRef?: string;
  /** True for the Group-subject shared cohort note (not a per-client fan-out copy). */
  isSharedCohortNote?: boolean;
}): AttendanceRowMeta[] {
  const serviceType = getServiceTypeCode(input.episodeOfCare ?? ({} as EpisodeOfCare));
  if (isSharedEpisodeServiceType(serviceType)) {
    return rowsFromParticipants(input.appointment, input.episodeOfCare, true);
  }

  const noteSubjectIsGroup = input.notePatientRef?.startsWith('Group/');
  const noteIsPatientCopy =
    !input.isSharedCohortNote &&
    !noteSubjectIsGroup &&
    Boolean(input.notePatientRef?.startsWith('Patient/'));
  if (
    (input.isSharedCohortNote || noteSubjectIsGroup || isCohortGroupAppointment(input.appointment)) &&
    !noteIsPatientCopy
  ) {
    return rowsFromParticipants(input.appointment, undefined, false);
  }

  // Per-client group/IOP notes and cohort patient-copy notes stay single-patient.
  const cohortPatientCopy =
    noteIsPatientCopy && isCohortGroupAppointment(input.appointment);
  if (isPerClientNoteServiceType(serviceType) || cohortPatientCopy) {
    const patientId = resolveNotePatientId(input);
    if (!patientId) return [];
    const fallback =
      input.encounter?.subject?.display ??
      input.episodeOfCare?.patient?.display ??
      input.notePatientRef;
    return [rowForPatientId(input.appointment, patientId, fallback)];
  }

  // Individual (and similar): note subject + RelatedPerson participants on the appointment.
  const patientId = resolveNotePatientId(input);
  if (!patientId) {
    return rowsFromParticipants(input.appointment, input.episodeOfCare, true);
  }
  const fallback =
    input.encounter?.subject?.display ??
    input.episodeOfCare?.patient?.display ??
    input.notePatientRef;
  const byId = new Map<string, AttendanceRowMeta>();
  byId.set(patientId, rowForPatientId(input.appointment, patientId, fallback));
  for (const p of getAllClientParticipantsFromAppointment(input.appointment)) {
    if (!p.actorReference?.startsWith('RelatedPerson/')) continue;
    const relatedId = participantIdFromActorReference(p.actorReference);
    if (!relatedId || byId.has(relatedId)) continue;
    byId.set(relatedId, {
      patientId: relatedId,
      displayName: p.displayName,
    });
  }
  return [...byId.values()];
}
